/* eslint-disable react/require-default-props */
import React, { useEffect, useMemo, useState } from 'react';

import api from 'services/api';
import CentralIaExpert from 'components/organisms/central_ia_expert';
import CentralIaPromptModal from 'components/organisms/central_ia_prompt_modal';

import { getExpertPhoto } from './expert_photos';
import { Container } from './styles';

interface Category {
  slug: string;
  name: string;
}

interface ExpertPrompt {
  slug: string;
  title: string;
  summary: string;
  topics: string[];
}

interface Expert {
  slug: string;
  name: string;
  role: string;
  bio: string;
  photoUrl: string;
  instagram: string;
  promptsCount: number;
  prompts: ExpertPrompt[];
}

interface BibliotecaData {
  categories: Category[];
  experts: Expert[];
  professionalsCount: number;
  promptsCount: number;
}

const DEFAULT_DESCRIPTION =
  'Prompts estruturados e validados por especialistas em contratações públicas, ' +
  'desenvolvidos para acelerar análises, decisões e execuções no dia a dia. Utilize-os como ' +
  'base para iniciar conversas no Central IA e personalize os campos conforme o seu contexto.';

interface CentralIaLibraryProps {
  onUsePrompt: (body: string, providerId?: string, model?: string) => void;
  brand?: string;
  description?: string;
  showProviderConfig?: boolean;
  background?: string;
}

const getInitials = (name: string) =>
  name
    .split(' ')
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

const normalizeTopics = (topics: any): string[] =>
  Array.isArray(topics)
    ? topics.map((topic: any) =>
        typeof topic === 'string' ? topic : topic?.slug ?? topic?.name ?? '',
      )
    : [];

const normalizeBiblioteca = (data: any): BibliotecaData => ({
  categories: Array.isArray(data?.categories)
    ? data.categories.map((category: any) => ({ slug: category.slug, name: category.name }))
    : [],
  experts: Array.isArray(data?.experts)
    ? data.experts.map((expert: any) => {
        const photo: string = expert.photo_url ?? '';

        const slug =
          expert.slug ||
          (photo && !/^https?:\/\//.test(photo)
            ? photo.replace(/^.*\//, '').replace(/\.[^.]+$/, '')
            : '');
        return {
          slug,
          name: expert.name,
          role: expert.role,
          bio: expert.bio,
          photoUrl: photo,
          instagram: expert.instagram_handle,
          promptsCount: expert.prompts_count,
          prompts: Array.isArray(expert.prompts)
            ? expert.prompts.map((prompt: any) => ({
                slug: prompt.slug ?? '',
                title: prompt.title ?? '',
                summary: prompt.summary ?? '',
                topics: normalizeTopics(prompt.topics),
              }))
            : [],
        };
      })
    : [],
  professionalsCount: data?.stats?.professionals_count ?? 0,
  promptsCount: data?.stats?.prompts_count ?? 0,
});

const CentralIaLibrary: React.FC<CentralIaLibraryProps> = ({
  onUsePrompt,
  brand = 'Central IA',
  description = DEFAULT_DESCRIPTION,
  showProviderConfig = true,
  background,
}) => {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [data, setData] = useState<BibliotecaData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expertSlug, setExpertSlug] = useState<string | null>(null);
  const [promptSlug, setPromptSlug] = useState<string | null>(null);
  const [usingSlug, setUsingSlug] = useState<string | null>(null);
  const [copyBusySlug, setCopyBusySlug] = useState<string | null>(null);
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    api
      .get('/chatbots/central_ia/biblioteca')
      .then((response) => {
        if (active) setData(normalizeBiblioteca(response.data));
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!copiedSlug) return undefined;
    const timeoutId = window.setTimeout(() => setCopiedSlug(null), 1800);
    return () => window.clearTimeout(timeoutId);
  }, [copiedSlug]);

  const experts = data?.experts ?? [];
  const categories = data?.categories ?? [];

  const term = search.trim().toLowerCase();
  const hasQuery = term.length > 0 || activeCategory !== null;

  const categoryNameBySlug = useMemo(() => {
    const map: Record<string, string> = {};
    categories.forEach((category) => {
      map[category.slug] = category.name;
    });
    return map;
  }, [categories]);

  const allPrompts = useMemo<ExpertPrompt[]>(
    () => experts.flatMap((expert) => expert.prompts),
    [experts],
  );

  const filteredPrompts = useMemo(
    () =>
      allPrompts.filter((prompt) => {
        const matchesSearch =
          !term ||
          prompt.title.toLowerCase().includes(term) ||
          prompt.summary.toLowerCase().includes(term);
        const matchesCategory = !activeCategory || prompt.topics.includes(activeCategory);
        return matchesSearch && matchesCategory;
      }),
    [allPrompts, term, activeCategory],
  );

  const filteredExperts = useMemo(
    () =>
      experts.filter((expert) => {
        const matchesSearch =
          !term ||
          expert.name.toLowerCase().includes(term) ||
          expert.role.toLowerCase().includes(term) ||
          expert.bio.toLowerCase().includes(term);
        const matchesCategory =
          !activeCategory ||
          expert.prompts.some((prompt) => prompt.topics.includes(activeCategory));
        return matchesSearch && matchesCategory;
      }),
    [experts, term, activeCategory],
  );

  const fetchPromptBody = async (slug: string): Promise<string> => {
    const response = await api.get(`/chatbots/central_ia/biblioteca/prompts/${slug}`);
    return response.data?.body ?? '';
  };

  const handleUsePromptCard = async (slug: string) => {
    setUsingSlug(slug);
    try {
      const body = await fetchPromptBody(slug);
      if (body) onUsePrompt(body);
    } catch {
      // ignora — falha ao carregar o prompt.
    } finally {
      setUsingSlug(null);
    }
  };

  const handleCopyPromptCard = async (slug: string) => {
    setCopyBusySlug(slug);
    try {
      const body = await fetchPromptBody(slug);
      await navigator.clipboard.writeText(body);
      setCopiedSlug(slug);
    } catch {
      // clipboard indisponivel ou falha — ignora.
    } finally {
      setCopyBusySlug(null);
    }
  };

  const handleUse = (body: string, providerId: string, model: string) => {
    setPromptSlug(null);
    onUsePrompt(body, providerId, model);
  };

  const promptModal = promptSlug && (
    <CentralIaPromptModal
      slug={promptSlug}
      onClose={() => setPromptSlug(null)}
      onUse={handleUse}
      showProviderConfig={showProviderConfig}
    />
  );

  const renderExpertCard = (expert: Expert) => (
    <div
      key={expert.slug}
      role="button"
      tabIndex={0}
      className="expert-card d-flex flex-column text-left w-100"
      onClick={() => setExpertSlug(expert.slug)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          setExpertSlug(expert.slug);
        }
      }}
    >
      <div className="expert-head d-flex align-items-center">
        {getExpertPhoto(expert.photoUrl) ? (
          <img
            className="expert-avatar rounded-circle"
            src={getExpertPhoto(expert.photoUrl)}
            alt={expert.name}
          />
        ) : (
          <div className="expert-avatar rounded-circle d-flex align-items-center justify-content-center">
            {getInitials(expert.name)}
          </div>
        )}
        <div>
          <div className="expert-name font-weight-bold">{expert.name}</div>
          <div className="expert-role">{expert.role}</div>
        </div>
      </div>
      <p className="expert-bio">{expert.bio}</p>
      <div className="expert-foot d-flex align-items-center justify-content-between">
        <span className="prompts-badge d-inline-flex align-items-center rounded-pill">
          <i className="material-icons">auto_awesome</i>
          {expert.promptsCount} prompts
        </span>
        {expert.instagram && (
          <a
            className="instagram d-inline-flex align-items-center"
            href={`https://www.instagram.com/${expert.instagram.replace(/^@/, '')}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => event.stopPropagation()}
          >
            <i className="material-icons">alternate_email</i>
            {expert.instagram.replace(/^@/, '')}
          </a>
        )}
      </div>
    </div>
  );

  const renderPromptCard = (prompt: ExpertPrompt) => (
    <div key={prompt.slug} className="prompt-card">
      <div className="prompt-card-head">
        <div className="prompt-title">{prompt.title}</div>
        {prompt.topics[0] && (
          <span className="topic-chip">
            {categoryNameBySlug[prompt.topics[0]] ?? prompt.topics[0]}
          </span>
        )}
      </div>
      {prompt.summary && <p className="prompt-summary">{prompt.summary}</p>}
      <div className="prompt-card-foot">
        <button
          type="button"
          className="use-button"
          onClick={() => handleUsePromptCard(prompt.slug)}
          disabled={usingSlug === prompt.slug}
        >
          <i className="material-icons">auto_awesome</i>
          {usingSlug === prompt.slug ? 'Abrindo...' : 'Usar no chat'}
        </button>
        <button
          type="button"
          className="copy-button"
          onClick={() => handleCopyPromptCard(prompt.slug)}
          disabled={copyBusySlug === prompt.slug}
          title="Copiar prompt"
          aria-label="Copiar prompt"
        >
          <i className="material-icons">{copiedSlug === prompt.slug ? 'check' : 'content_copy'}</i>
        </button>
        <button type="button" className="full-button" onClick={() => setPromptSlug(prompt.slug)}>
          Ver completo
          <i className="material-icons">chevron_right</i>
        </button>
      </div>
    </div>
  );

  if (expertSlug) {
    return (
      <>
        <CentralIaExpert
          slug={expertSlug}
          categories={categories}
          onBack={() => setExpertSlug(null)}
          onOpenPrompt={setPromptSlug}
          onUsePrompt={onUsePrompt}
          background={background}
        />
        {promptModal}
      </>
    );
  }
  return (
    <>
      <Container style={background ? { background } : undefined}>
        <div className="library-inner mx-auto">
          <span className="eyebrow d-block text-uppercase">{brand}</span>
          <h1 className="library-title font-weight-bold">Biblioteca de Prompts</h1>
          <p className="library-desc">{description}</p>

          <div className="library-search d-flex align-items-center rounded-pill w-100">
            <i className="material-icons">search</i>
            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar por especialista ou título do prompt..."
            />
          </div>

          {categories.length > 0 && (
            <div className="library-categories d-flex flex-wrap">
              {categories.map((category) => (
                <button
                  key={category.slug}
                  type="button"
                  className={`category-chip rounded-pill ${
                    activeCategory === category.slug ? 'active' : ''
                  }`}
                  onClick={() =>
                    setActiveCategory((current) =>
                      current === category.slug ? null : category.slug,
                    )
                  }
                >
                  {category.name}
                </button>
              ))}
            </div>
          )}
          {loading ? (
            <div className="library-empty">Carregando...</div>
          ) : error ? (
            <div className="library-empty">Não foi possível carregar a biblioteca.</div>
          ) : hasQuery ? (
            filteredExperts.length === 0 && filteredPrompts.length === 0 ? (
              <div className="library-empty">Nenhum resultado encontrado.</div>
            ) : (
              <>
                {filteredExperts.length > 0 && (
                  <section className="results-section">
                    <div className="experts-header d-flex align-items-baseline justify-content-between">
                      <h2 className="font-weight-bold">Especialistas</h2>
                      <span className="experts-count">
                        {filteredExperts.length}{' '}
                        {filteredExperts.length === 1 ? 'especialista' : 'especialistas'}
                      </span>
                    </div>
                    <div className="experts-grid">{filteredExperts.map(renderExpertCard)}</div>
                  </section>
                )}
                {filteredPrompts.length > 0 && (
                  <section className="results-section">
                    <div className="experts-header d-flex align-items-baseline justify-content-between">
                      <h2 className="font-weight-bold">Prompts</h2>
                      <span className="experts-count">
                        {filteredPrompts.length}{' '}
                        {filteredPrompts.length === 1 ? 'prompt' : 'prompts'}
                      </span>
                    </div>
                    <div className="prompts-grid">{filteredPrompts.map(renderPromptCard)}</div>
                  </section>
                )}
              </>
            )
          ) : (
            <>
              <div className="experts-header d-flex align-items-baseline justify-content-between">
                <h2 className="font-weight-bold">Especialistas parceiros</h2>
                {data && (
                  <span className="experts-count">
                    {data.professionalsCount} profissionais · {data.promptsCount} prompts
                  </span>
                )}
              </div>
              {experts.length === 0 ? (
                <div className="library-empty">Nenhum especialista encontrado.</div>
              ) : (
                <div className="experts-grid">{experts.map(renderExpertCard)}</div>
              )}
            </>
          )}
        </div>
      </Container>
      {promptModal}
    </>
  );
};

export default CentralIaLibrary;
